import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid
} from '@mui/material';
import { useAuth } from '../auth/useAuth';
import AdminLayout from '../components/AdminLayout';

const TransactionsList = () => {
  const { api } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);
  const [filters, setFilters] = useState({
    paymentMethod: 'all',
    status: 'all',
    search: ''
  });

  useEffect(() => {
    fetchTransactions();
  }, [page, rowsPerPage, filters]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      // הוסף פילטרים אם נבחרו
      if (filters.paymentMethod !== 'all') {
        params.paymentMethod = filters.paymentMethod;
      }
      if (filters.status !== 'all') {
        params.status = filters.status;
      }
      if (filters.search) {
        params.search = filters.search;
      }

      const response = await api.get('/transactions', { params });
      console.log('🔍 Transactions data from server:', response.data.transactions);
      setTransactions(response.data.transactions);
      setTotalRows(response.data.total);
    } catch (error) {
      console.error('שגיאה בטעינת העסקאות:', error);
      setError('שגיאה בטעינת העסקאות');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodText = (method) => {
    switch (method) {
      case 'cash': return 'מזומן';
      case 'paypal': return 'פאייכלה';
      case 'credit': return 'אשראי';
      default: return method;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'בהמתנה';
      case 'completed': return 'הושלם';
      case 'failed': return 'נכשל';
      case 'refunded': return 'הוחזר';
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'refunded': return 'info';
      default: return 'default';
    }
  };

  const getPaymentMethodColor = (method) => {
    switch (method) {
      case 'cash': return 'success';
      case 'paypal': return 'info';
      case 'credit': return 'primary';
      default: return 'default';
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setPage(0);
  };

  const getTotalAmount = () => {
    return transactions
      .filter(t => t.status === 'completed')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);
  };

  if (loading && transactions.length === 0) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Container maxWidth="xl">
        <Typography variant="h4" gutterBottom align="center">
          ניהול עסקאות
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* סיכום כספי */}
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography variant="h5" align="center" color="primary">
                💰 סה"כ הכנסות: ${getTotalAmount().toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>אמצעי תשלום</InputLabel>
                    <Select
                      value={filters.paymentMethod}
                      onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                      label="אמצעי תשלום"
                    >
                      <MenuItem value="all">הכל</MenuItem>
                      <MenuItem value="cash">מזומן</MenuItem>
                      <MenuItem value="paypal">פאייכלה</MenuItem>
                      <MenuItem value="credit">אשראי</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>סטטוס</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      label="סטטוס"
                    >
                      <MenuItem value="all">הכל</MenuItem>
                      <MenuItem value="pending">בהמתנה</MenuItem>
                      <MenuItem value="completed">הושלם</MenuItem>
                      <MenuItem value="failed">נכשל</MenuItem>
                      <MenuItem value="refunded">הוחזר</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="חיפוש"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    placeholder="שם לקוח, מספר עסקה..."
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>תאריך</strong></TableCell>
                <TableCell><strong>לקוח</strong></TableCell>
                <TableCell><strong>מספר הזמנה</strong></TableCell>
                <TableCell align="center"><strong>סכום</strong></TableCell>
                <TableCell align="center"><strong>אמצעי תשלום</strong></TableCell>
                <TableCell align="center"><strong>סטטוס</strong></TableCell>
                <TableCell><strong>מעבד</strong></TableCell>
                <TableCell><strong>הערות</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(transaction.createdAt).toLocaleDateString('he-IL')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(transaction.createdAt).toLocaleTimeString('he-IL')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {transaction.tourist ? (
                      <Typography variant="body2">
                        {transaction.tourist.PRATI || transaction.tourist.FIRST_NAME || ''} {transaction.tourist.MISHPACHA || transaction.tourist.LAST_NAME || ''}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        לא זמין
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {transaction.tourist?.MISPAR_HAZMANA ? (
                      <Typography variant="body2">
                        {transaction.tourist.MISPAR_HAZMANA}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        לא זמין
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {transaction.discountAmount && transaction.discountAmount > 0 ? (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                          ${parseFloat(transaction.originalAmount).toFixed(2)}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="success.main">
                          ${parseFloat(transaction.amount).toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="error.main">
                          הנחה: ${parseFloat(transaction.discountAmount).toFixed(2)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body1" fontWeight="bold">
                        ${parseFloat(transaction.amount).toFixed(2)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={getPaymentMethodText(transaction.paymentMethod)}
                      color={getPaymentMethodColor(transaction.paymentMethod)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={getStatusText(transaction.status)}
                      color={getStatusColor(transaction.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {transaction.processedBy ? (
                      <Typography variant="body2">
                        {transaction.processedBy.firstName} {transaction.processedBy.lastName}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        לא זמין
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {transaction.notes || '-'}
                    </Typography>
                    {transaction.discountReason && (
                      <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                        סיבת הנחה: {transaction.discountReason}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="שורות בעמוד:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} מתוך ${count}`}
          />
        </TableContainer>

        {loading && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        )}
      </Container>
    </AdminLayout>
  );
};

export default TransactionsList;
